import {
  INVENTORY_STATE,
  INVENTORY_TERM,
  canConfirmInventoryArrival,
  compareInventoryLotNames,
  getInventoryState,
  normalizeInventoryLotName
} from './inventorySelectors'
import { getRealName } from '../utils/orderIdentity'
import { toTorontoDateTimeInput } from '../components/module/special-components/Inventory/inventoryDateTime'

export const INVENTORY_EXPORT_MODE = Object.freeze({
  LOTS: 'lots',
  BILLING: 'billing'
})

export const UNASSIGNED_EXPORT_LOT = '__unassigned__'

const SHEET_NAME_MAX_LENGTH = 31
const INVALID_SHEET_CHARACTERS = /[\\/?*[\]:]/g
const DAY_MS = 24 * 60 * 60 * 1000

const orderIdOf = order => String(order['.key'] || order.orderNumber || order.key || '')

const exportDate = timestamp => {
  const value = Number(timestamp)
  return value > 0 ? toTorontoDateTimeInput(value).replace('T', ' ') : ''
}

export const exportLotKey = (order, lotNames) => {
  const lot = normalizeInventoryLotName(order && order.inventoryLot)
  return lotNames.indexOf(lot) !== -1 ? lot : UNASSIGNED_EXPORT_LOT
}

export const exportBillingKey = order => {
  const payer = String((order && order.payer) || '').trim()
  return payer || `name:${getRealName(order, 'payer')}`
}

export const inventoryExportLotOptions = (orders, lotNames) => {
  const counts = orders.reduce((result, order) => {
    const key = exportLotKey(order, lotNames)
    result[key] = (result[key] || 0) + 1
    return result
  }, {})
  const options = lotNames.map(name => ({value: name, label: name, count: counts[name] || 0}))
  if (counts[UNASSIGNED_EXPORT_LOT]) {
    options.push({value: UNASSIGNED_EXPORT_LOT, label: '', count: counts[UNASSIGNED_EXPORT_LOT]})
  }
  return options
}

export const inventoryExportBillingOptions = orders => {
  const byKey = {}
  orders.forEach(order => {
    const key = exportBillingKey(order)
    if (!byKey[key]) byKey[key] = {value: key, label: getRealName(order, 'payer'), count: 0}
    byKey[key].count += 1
  })
  return Object.keys(byKey)
    .map(key => byKey[key])
    .sort((left, right) => left.label.localeCompare(right.label, undefined, {sensitivity: 'base'}))
}

export const inventoryExportColumns = ({t, lotNames, now}) => {
  const stateLabel = order => {
    if (canConfirmInventoryArrival(order)) return t('Inventory.filter_to_confirm')
    const state = getInventoryState(order)
    if (state === INVENTORY_STATE.IN_INVENTORY) return t('Inventory.state_inside')
    if (state === INVENTORY_STATE.LEFT_AWAITING_BILLING) return t('Inventory.state_left')
    return ''
  }
  const termLabel = order => {
    if (order.inventoryTerm === INVENTORY_TERM.LONG_TERM) return t('Inventory.long_term')
    if (order.inventoryTerm === INVENTORY_TERM.SHORT_TERM) return t('Inventory.short_term')
    return ''
  }
  const daysInStorage = order => {
    const entered = Number(order.dateEntered)
    if (!(entered > 0)) return ''
    const end = Number(order.dateLeft) > 0 ? Number(order.dateLeft) : now
    return Math.max(0, Math.floor((end - entered) / DAY_MS))
  }
  const lotLabel = order => {
    const key = exportLotKey(order, lotNames)
    return key === UNASSIGNED_EXPORT_LOT ? t('Inventory.unassigned_lot') : key
  }

  return [
    {header: t('Inventory.lot'), width: 10, value: lotLabel},
    {header: t('Inventory.order'), width: 12, value: orderIdOf},
    {header: t('Inventory.vin'), width: 20, value: order => order.vin || ''},
    {header: t('Inventory.year'), width: 7, value: order => order.year || ''},
    {header: t('Inventory.make'), width: 14, value: order => order.make || ''},
    {header: t('Inventory.model'), width: 16, value: order => order.model || ''},
    {header: t('Inventory.trim'), width: 14, value: order => order.trim || ''},
    {header: t('Inventory.billing_name'), width: 28, value: order => getRealName(order, 'payer')},
    {header: t('Inventory.storage_term'), width: 13, value: termLabel},
    {header: t('Inventory.inventory_state'), width: 22, value: stateLabel},
    {header: t('Inventory.date_entered'), width: 17, value: order => exportDate(order.dateEntered)},
    {header: t('Inventory.date_left'), width: 17, value: order => exportDate(order.dateLeft)},
    {header: t('Inventory.export_days_in_storage'), width: 10, value: daysInStorage},
    {header: t('Inventory.order_info.source_order'), width: 12, value: order => order.sourceOrderId || ''},
    {header: t('Inventory.source_reference'), width: 14, value: order => order.numRef || ''},
    {header: t('Inventory.dropoff_person'), width: 20, value: order => order.inventoryDropOffPerson || ''},
    {header: t('Inventory.dropoff_organization'), width: 22, value: order => order.inventoryDropOffOrganization || ''},
    {header: t('Inventory.comments'), width: 30, value: order => order.comment || ''}
  ]
}

export const safeSheetName = (value, usedNames) => {
  const base = String(value || '')
    .replace(INVALID_SHEET_CHARACTERS, ' ')
    .replace(/^'+|'+$/g, '')
    .trim()
    .slice(0, SHEET_NAME_MAX_LENGTH) || 'Sheet'
  let name = base
  let index = 2
  while (usedNames.indexOf(name.toLowerCase()) !== -1) {
    const suffix = ` (${index})`
    name = base.slice(0, SHEET_NAME_MAX_LENGTH - suffix.length) + suffix
    index += 1
  }
  usedNames.push(name.toLowerCase())
  return name
}

const byDateEntered = (left, right) => Number(left.dateEntered || 0) - Number(right.dateEntered || 0)

// Lot mode: one sheet per selected lot. Billing mode: one sheet for the account, ordered by lot.
export const buildInventoryExportSheets = ({orders, lotNames, mode, selectedLots, billingKey, t}) => {
  const usedNames = []
  const unassignedLabel = t('Inventory.unassigned_lot')

  if (mode === INVENTORY_EXPORT_MODE.BILLING) {
    const matching = orders.filter(order => exportBillingKey(order) === billingKey)
    if (!matching.length) return []
    const lotRank = order => {
      const key = exportLotKey(order, lotNames)
      return key === UNASSIGNED_EXPORT_LOT ? lotNames.length : lotNames.indexOf(key)
    }
    return [{
      name: safeSheetName(getRealName(matching[0], 'payer') || t('Inventory.billing_name'), usedNames),
      orders: matching.slice().sort((left, right) => lotRank(left) - lotRank(right) || byDateEntered(left, right))
    }]
  }

  const selected = Array.isArray(selectedLots) ? selectedLots : []
  const sheetLots = lotNames
    .filter(name => selected.indexOf(name) !== -1)
    .sort(compareInventoryLotNames)
    .concat(selected.indexOf(UNASSIGNED_EXPORT_LOT) !== -1 ? [UNASSIGNED_EXPORT_LOT] : [])

  return sheetLots
    .map(lot => ({
      lot,
      orders: orders.filter(order => exportLotKey(order, lotNames) === lot).sort(byDateEntered)
    }))
    .filter(sheet => sheet.orders.length)
    .map(sheet => ({
      name: safeSheetName(sheet.lot === UNASSIGNED_EXPORT_LOT ? unassignedLabel : sheet.lot, usedNames),
      orders: sheet.orders
    }))
}

export const inventoryExportFileName = (label, now) => {
  const slug = String(label || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase()
  const date = toTorontoDateTimeInput(now).slice(0, 10)
  return ['inventory', slug, date].filter(Boolean).join('_') + '.xlsx'
}

// Served from static/ and injected on demand: the webpack vendor chunk would otherwise ship it on every page load.
const EXCELJS_SCRIPT = '/static/vendor/exceljs-4.4.0.min.js'
let excelJsPromise = null

const loadExcelJS = () => {
  if (window.ExcelJS) return Promise.resolve(window.ExcelJS)
  if (!excelJsPromise) {
    excelJsPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.src = EXCELJS_SCRIPT
      script.async = true
      const fail = message => {
        excelJsPromise = null
        script.remove()
        reject(new Error(message))
      }
      script.onload = () => window.ExcelJS ? resolve(window.ExcelJS) : fail('ExcelJS did not load')
      script.onerror = () => fail(`Could not load ${EXCELJS_SCRIPT}`)
      document.head.appendChild(script)
    })
  }
  return excelJsPromise
}

export const downloadInventoryWorkbook = async ({sheets, columns, fileName}) => {
  const ExcelJS = await loadExcelJS()
  const workbook = new ExcelJS.Workbook()
  workbook.created = new Date()

  sheets.forEach(sheet => {
    const worksheet = workbook.addWorksheet(sheet.name, {views: [{state: 'frozen', ySplit: 1}]})
    worksheet.columns = columns.map((column, index) => ({key: String(index), header: column.header, width: column.width}))
    sheet.orders.forEach(order => {
      worksheet.addRow(columns.map(column => column.value(order)))
    })
    worksheet.getRow(1).font = {bold: true}
    worksheet.autoFilter = {from: {row: 1, column: 1}, to: {row: 1, column: columns.length}}
  })

  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'})
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
